import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useLoginTwoFactorMutation } from 'app/auth/api/auth-api';
import { OtpInput } from 'components/OtpInput/OtpInput';
import useAppLanguage from 'hooks/useAppLanguage';
import usePageConfig from 'hooks/usePageConfig';
import { loadTokenFromStorage } from 'utils/token';
import { z } from 'zod';
import messages from '../messages';

export const Route = createFileRoute('/auth/login/2fa')({
  component: LoginTwoFactor,
  validateSearch: zodValidator(
    z.object({
      redirect: z.string().optional(),
    }),
  ),
});

export default function LoginTwoFactor() {
  const language = useAppLanguage();
  const pageConfig = usePageConfig();
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const [loginTwoFactor, loginTwoFactorData] = useLoginTwoFactorMutation();

  const loginTwoFactorMessages = messages[language].loginTwoFactor;

  const errorMessage = loginTwoFactorData.error?.message?.includes(
    '2FA verification failed',
  )
    ? loginTwoFactorMessages.twoFactorErrorMessage
    : loginTwoFactorData.error?.message;

  async function handleOtpCodeChanged(pin: string) {
    loginTwoFactorData.reset();

    if (pin.length === 6) {
      const token = loadTokenFromStorage()!;
      const res = await loginTwoFactor({ pin, token });

      if (!res.error) {
        navigate({ to: redirect || '/' });
      }
    }
  }

  return (
    <div className="flex flex-col bg-ghost-white">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="font-semibold leading-9 mt-10 text-2.5x1 text-black text-center tracking-wide">
          {loginTwoFactorMessages.heading}
        </h2>
        <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
          {loginTwoFactorMessages.subHeading}
        </p>
      </div>
      <div className="mt-16 mb-16 sm:mx-auto sm:w-full text-center">
        <OtpInput
          isNumberInput
          length={6}
          onChangeOtp={handleOtpCodeChanged}
          warning={loginTwoFactorData.isError}
          warningMessage={errorMessage}
        />
      </div>

      <div className="sm:mx-auto sm:w-full text-1.06x1">
        <div className="m-6">
          <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
            <p className="leading-9 m-6 text-black text-center">
              {loginTwoFactorMessages.havingTroubleLabel}
            </p>
            <p className="text-gray-900">
              {loginTwoFactorMessages.supportLabelBeforeEmail}&nbsp;
              <a
                href={`mailto:${pageConfig?.app?.supportEmail}`}
                className="underline">
                {pageConfig?.app?.supportEmail}
              </a>
            </p>
          </div>
          <div className="mt-6">
            <span className="block text-center">
              <Link
                to="/auth/reset-two-factor"
                className="font-semibold text-right text-vivid-blue underline hover:no-underline">
                {loginTwoFactorMessages.resetTwoFactorLink}
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
